/**
 * "Registrar no prontuário" — texto que uma mensagem do chat vira na ficha.
 *
 * Funções puras (sem Firebase) pra poder testar o formato à parte. O texto
 * leva quem mandou, onde e quando — no prontuário a mensagem fica sozinha,
 * fora da conversa, e "agenda revisão pro dia 19" sem autor nem data não diz
 * nada daqui a um ano. Se a mensagem respondia outra, a pergunta vai junto:
 * "ok, pode dilatar" só faz sentido com o "dilatou logo ou espero?" acima.
 */
import type { ChatMessageData } from '@/services/firebase/chat';

/** Rótulo do registro na coluna do prontuário. */
export const CHAT_RECORD_TITLE = 'Recado do chat';

/** Só mensagem com texto vai pro prontuário (imagem/arquivo/atenção não). */
export function canRegisterChatInRecord(m: Pick<ChatMessageData, 'type' | 'text'>): boolean {
  return (!m.type || m.type === 'text') && !!m.text?.trim();
}

/** Médico e admin — os únicos que gravam evolução (firestore.rules → records). */
export function roleCanRegisterChatInRecord(role: string | undefined | null): boolean {
  return role === 'MEDICO' || role === 'ADMIN';
}

/** "07/10/2026 11:28" no fuso da clínica. */
export function formatChatRecordWhen(ts: number): string {
  // toLocaleString põe vírgula entre data e hora ("07/10/2026, 11:28").
  return new Date(ts)
    .toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/Sao_Paulo',
    })
    .replace(', ', ' ');
}

/**
 * Texto que vai pro prontuário (o médico ainda revisa antes de gravar):
 *
 *   Recado do chat (@Clinoft BS) — Leopoldo Pinheiro, 07/10/2026 11:28
 *   Em resposta a Clinoft BS: "dilatou logo ou espero?"
 *
 *   agenda revisão pro dia 19 ou 21
 */
export function buildChatRecordText(
  m: Pick<ChatMessageData, 'text' | 'senderName' | 'timestamp' | 'replyToSenderName' | 'replyToText'>,
  channelLabel?: string,
): string {
  const where = channelLabel?.trim() ? ` (${channelLabel.trim()})` : '';
  const header = `${CHAT_RECORD_TITLE}${where} — ${m.senderName}, ${formatChatRecordWhen(m.timestamp)}`;
  const reply = m.replyToText?.trim()
    ? `\nEm resposta a ${m.replyToSenderName || 'mensagem'}: "${m.replyToText.trim()}"`
    : '';
  return `${header}${reply}\n\n${m.text.trim()}`;
}
